/**
 * Firebase Firestore Service Module
 * 
 * This module provides a comprehensive interface for interacting with Firebase Firestore database.
 * It handles all CRUD operations for different entities in the trip planning system:
 * - Trips
 * - Days
 * - Places
 * - Travel
 * - Accommodation
 * 
 * Database Structure:
 * users/
 *   ├── {userId}/
 *   │   ├── trips/
 *   │   │   ├── {tripId}/
 *   │   │   │   ├── days/
 *   │   │   │   ├── places/
 *   │   │   │   ├── travel/
 *   │   │   │   └── accommodation/
 */

// src/firebase/firestoreService.js
import { db, storage } from './firebaseInit';
// eslint-disable-next-line import/named
import { collection, doc, addDoc, getDoc, getDocs, updateDoc, deleteDoc, query, orderBy, writeBatch, deleteField } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { eventBridgeService, EVENT_TYPES } from '../services/eventService';

/**
 * Trip Management Section
 * Handles all operations related to trip documents in Firestore
 */

/**
 * Retrieves a specific trip by ID
 * @param {string} userId - The ID of the user who owns the trip
 * @param {string} tripId - The ID of the trip to retrieve
 * @returns {Promise<Object>} Trip data with its ID
 * @throws {Error} If trip not found or on Firebase error
 */
export const getTrip = async (userId, tripId) => {
  try {
    const tripRef = doc(db, 'users', userId, 'trips', tripId);
    const tripDoc = await getDoc(tripRef);
    if (tripDoc.exists()) {
      return {
        id: tripDoc.id,
        ...tripDoc.data()
      };
    } else {
      throw new Error('Trip not found');
    }
  } catch (error) {
    console.error('Error getting trip: ', error);
    throw error;
  }
};

/**
 * Creates a new trip for a user
 * @param {string} userId - The ID of the user creating the trip
 * @param {Object} tripData - The trip data to store
 * @returns {Promise<string>} The ID of the newly created trip
 */
export const addTrip = async (userId, tripData) => {
  try {
    const userTripsRef = collection(db, 'users', userId, 'trips');
    const docRef = await addDoc(userTripsRef, {
      ...tripData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    return docRef.id;
  } catch (error) {
    console.error('Error adding trip: ', error);
    throw error;
  }
};

/**
 * Retrieves all trips for a user, ordered by creation date
 * @param {string} userId - The ID of the user
 * @returns {Promise<Array>} Array of trip objects with their IDs
 */
export const getTrips = async (userId) => {
  try {
    const userTripsRef = collection(db, 'users', userId, 'trips');
    const q = query(userTripsRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting trips: ', error);
    throw error;
  }
};

/**
 * Retrieves all shared trip pointers for a user
 * @param {string} userId - The ID of the user (viewer)
 * @returns {Promise<Array>} Array of shared trip pointer objects
 */
export const getSharedTrips = async (userId) => {
  try {
    const sharedTripsRef = collection(db, 'users', userId, 'shared_trips');
    const q = query(sharedTripsRef, orderBy('sharedAt', 'desc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting shared trips: ', error);
    // Return empty array instead of throwing to prevent blocking main trips
    return [];
  }
};

/**
 * Updates an existing trip
 * @param {string} userId - The ID of the user who owns the trip
 * @param {string} tripId - The ID of the trip to update
 * @param {Object} tripData - The new trip data
 */
export const updateTrip = async (userId, tripId, tripData) => {
  try {
    const tripRef = doc(db, 'users', userId, 'trips', tripId);
    await updateDoc(tripRef, {
      ...tripData,
      updatedAt: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error updating trip: ', error);
    throw error;
  }
};

/**
 * Deletes a trip and ALL associated data (sub-collections + Storage files)
 * This is the canonical trip deletion - use this for all trip deletions
 *
 * @param {string} userId - User ID
 * @param {string} tripId - Trip ID to delete
 * @returns {Promise<void>}
 */
export const deleteTrip = async (userId, tripId) => {
  try {
    console.log(`[deleteTrip] Starting comprehensive deletion for trip: ${tripId}`);

    // Step 1: Delete document files from Firebase Storage
    console.log('[deleteTrip] Step 1: Deleting document files from Storage...');
    const documentsRef = collection(db, 'users', userId, 'trips', tripId, 'documents');
    const documentsSnapshot = await getDocs(documentsRef);

    let storageFilesDeleted = 0;
    for (const docSnap of documentsSnapshot.docs) {
      const docData = docSnap.data();
      if (docData.storagePath) {
        try {
          const storageRef = ref(storage, docData.storagePath);
          await deleteObject(storageRef);
          storageFilesDeleted++;
          console.log(`[deleteTrip] Deleted storage file: ${docData.storagePath}`);
        } catch (storageError) {
          // File may already be deleted or not exist - log warning but continue
          console.warn(`[deleteTrip] Storage file not found or already deleted: ${docData.storagePath}`, storageError.message);
        }
      }
    }
    console.log(`[deleteTrip] Deleted ${storageFilesDeleted} storage files`);

    // Step 2: Delete all sub-collections using chunked batch writes.
    // 'links' and '_meta' added (Issue 9) — previously the connection registry
    // (often the largest subcollection in a trip) and its config survived trip
    // deletion as orphaned data. Firestore caps a batch at 500 operations, and
    // 'links' can hold more rows than every other subcollection combined, so writes
    // are chunked into 400-op batches (Issue N6) rather than one unbounded batch that
    // would silently fail to delete a large trip.
    console.log('[deleteTrip] Step 2: Deleting sub-collections...');
    const BATCH_LIMIT = 400;
    const collections = ['days', 'places', 'accommodation', 'travel', 'documents', 'links', '_meta'];

    let batch = writeBatch(db);
    let opsInBatch = 0;
    let subCollectionDocsDeleted = 0;

    const queueDelete = async (docRef) => {
      batch.delete(docRef);
      opsInBatch++;
      if (opsInBatch >= BATCH_LIMIT) {
        await batch.commit();
        batch = writeBatch(db);
        opsInBatch = 0;
      }
    };

    for (const collectionName of collections) {
      const collectionRef = collection(db, 'users', userId, 'trips', tripId, collectionName);
      const snapshot = await getDocs(collectionRef);

      for (const docSnap of snapshot.docs) {
        await queueDelete(docSnap.ref);
        subCollectionDocsDeleted++;
      }

      console.log(`[deleteTrip] Queued ${snapshot.size} documents from ${collectionName} for deletion`);
    }

    // Step 3: Delete the trip document itself
    console.log('[deleteTrip] Step 3: Deleting trip document...');
    const tripRef = doc(db, 'users', userId, 'trips', tripId);
    await queueDelete(tripRef);

    // Commit any remaining queued deletes
    if (opsInBatch > 0) {
      await batch.commit();
    }
    console.log(`[deleteTrip] Batch delete committed: ${subCollectionDocsDeleted} sub-collection documents + 1 trip document`);

    // Step 4: Publish TRIP_DELETED event for cross-package coordination
    console.log('[deleteTrip] Step 4: Publishing TRIP_DELETED event...');
    eventBridgeService.publish(EVENT_TYPES.TRIP_DELETED, {
      userId,
      tripId,
      timestamp: new Date().toISOString(),
      deletionStats: {
        storageFilesDeleted,
        subCollectionDocsDeleted,
        collectionsProcessed: collections.length
      }
    });

    console.log(`[deleteTrip] Trip deletion completed successfully: ${tripId}`);

  } catch (error) {
    console.error('[deleteTrip] Error during trip deletion:', error);
    throw error;
  }
};

/**
 * Day Management Section
 * Handles operations for individual days within a trip
 * Days are ordered entities that represent different dates in a trip's itinerary
 */

/**
 * Adds a new day to a trip
 * @param {string} userId - The ID of the user
 * @param {string} tripId - The ID of the trip
 * @param {Object} dayData - Day information including order and date
 * @returns {Promise<string>} The ID of the newly created day
 */
export const addDay = async (userId, tripId, dayData) => {
  try {
    const daysRef = collection(db, 'users', userId, 'trips', tripId, 'days');
    const docRef = await addDoc(daysRef, {
      ...dayData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    return docRef.id;
  } catch (error) {
    console.error('Error adding day: ', error);
    throw error;
  }
};

/**
 * Retrieves all days for a trip, ordered by their sequence
 * @param {string} userId - The ID of the user
 * @param {string} tripId - The ID of the trip
 * @returns {Promise<Array>} Array of day objects ordered by their 'order' field
 */
export const getDays = async (userId, tripId) => {
  try {
    const daysRef = collection(db, 'users', userId, 'trips', tripId, 'days');
    const q = query(daysRef, orderBy('order', 'asc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting days: ', error);
    throw error;
  }
};

/**
 * Retrieves a specific day by its ID
 * @param {string} userId - The ID of the user
 * @param {string} tripId - The ID of the trip
 * @param {string} dayId - The ID of the day to retrieve
 * @returns {Promise<Object|null>} Day object with its ID, or null if not found
 */
export const getDay = async (userId, tripId, dayId) => {
  try {
    const dayRef = doc(db, 'users', userId, 'trips', tripId, 'days', dayId);
    const dayDoc = await getDoc(dayRef);

    if (!dayDoc.exists()) {
      return null;
    }

    return {
      id: dayDoc.id,
      ...dayDoc.data()
    };
  } catch (error) {
    console.error('Error getting day by ID: ', error);
    throw error;
  }
};

export const updateDay = async (userId, tripId, dayId, dayData) => {
  try {
    const dayRef = doc(db, 'users', userId, 'trips', tripId, 'days', dayId);
    await updateDoc(dayRef, {
      ...dayData,
      updatedAt: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error updating day: ', error);
    throw error;
  }
};

export const deleteDay = async (userId, tripId, dayId) => {
  try {
    const dayRef = doc(db, 'users', userId, 'trips', tripId, 'days', dayId);
    await deleteDoc(dayRef);
  } catch (error) {
    console.error('Error deleting day: ', error);
    throw error;
  }
};

/**
 * Place Management Section
 * Handles operations for places/locations within a trip
 * Places represent destinations, attractions, or points of interest
 */

/**
 * Adds a new place to a trip
 * @param {string} userId - The ID of the user
 * @param {string} tripId - The ID of the trip
 * @param {Object} placeData - Place information including location details
 * @returns {Promise<string>} The ID of the newly created place
 */
export const addPlace = async (userId, tripId, placeData) => {
  try {
    const placesRef = collection(db, 'users', userId, 'trips', tripId, 'places');
    const docRef = await addDoc(placesRef, {
      ...placeData,
      resources: [], // Initialize empty resources array
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    return docRef.id;
  } catch (error) {
    console.error('Error adding place: ', error);
    throw error;
  }
};

/**
 * Retrieves all places for a trip
 * @param {string} userId - The ID of the user
 * @param {string} tripId - The ID of the trip
 * @returns {Promise<Array>} Array of place objects
 */
export const getPlaces = async (userId, tripId) => {
  try {
    const placesRef = collection(db, 'users', userId, 'trips', tripId, 'places');
    const querySnapshot = await getDocs(placesRef);

    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting places: ', error);
    throw error;
  }
};

/**
 * Retrieves a specific place by ID
 * @param {string} userId - The ID of the user
 * @param {string} tripId - The ID of the trip
 * @param {string} placeId - The ID of the place to retrieve
 * @returns {Promise<Object|null>} Place object with its ID, or null if not found
 */
export const getPlaceById = async (userId, tripId, placeId) => {
  try {
    console.log(`Fetching place ${placeId}`);
    const placeRef = doc(db, 'users', userId, 'trips', tripId, 'places', placeId);
    const placeDoc = await getDoc(placeRef);

    if (!placeDoc.exists()) {
      console.log(`Place ${placeId} not found`);
      return null;
    }

    return {
      id: placeDoc.id,
      ...placeDoc.data()
    };
  } catch (error) {
    console.error('Error getting place by ID: ', error);
    throw error;
  }
};

export const updatePlace = async (userId, tripId, placeId, placeData) => {
  try {
    const placeRef = doc(db, 'users', userId, 'trips', tripId, 'places', placeId);
    // Remove resources from placeData if present to prevent accidental overwrite
    const { resources, ...updateData } = placeData;
    await updateDoc(placeRef, {
      ...updateData,
      updatedAt: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error updating place: ', error);
    throw error;
  }
};

export const deletePlace = async (userId, tripId, placeId) => {
  try {
    const placeRef = doc(db, 'users', userId, 'trips', tripId, 'places', placeId);
    await deleteDoc(placeRef);
  } catch (error) {
    console.error('Error deleting place: ', error);
    throw error;
  }
};

/**
 * Saves AI-generated resources to a place document
 * @param {string} userId - The ID of the user
 * @param {string} tripId - The ID of the trip
 * @param {string} placeId - The ID of the place
 * @param {Object} aiResources - AI resources object (categorized by type)
 * @returns {Promise<void>}
 */
export const saveAIResources = async (userId, tripId, placeId, aiResources) => {
  try {
    const placeRef = doc(db, 'users', userId, 'trips', tripId, 'places', placeId);
    await updateDoc(placeRef, {
      aiResources: aiResources,
      aiResourcesLastUpdated: new Date().toISOString()
    });
    console.log(`💾 [FirestoreService] Saved AI resources for place: ${placeId}`);
  } catch (error) {
    console.error('Error saving AI resources: ', error);
    throw error;
  }
};

/**
 * Retrieves AI-generated resources from a place document
 * @param {string} userId - The ID of the user
 * @param {string} tripId - The ID of the trip
 * @param {string} placeId - The ID of the place
 * @returns {Promise<Object|null>} AI resources object or null if not found
 */
export const getAIResources = async (userId, tripId, placeId) => {
  try {
    const placeRef = doc(db, 'users', userId, 'trips', tripId, 'places', placeId);
    const placeDoc = await getDoc(placeRef);
    if (placeDoc.exists()) {
      const data = placeDoc.data();
      return data.aiResources || null;
    }
    return null;
  } catch (error) {
    console.error('Error getting AI resources: ', error);
    throw error;
  }
};

/**
 * Travel Management Section
 * Handles operations for travel arrangements within a trip
 * Includes transportation details between places
 */

/**
 * Adds a new travel arrangement
 * @param {string} userId - The ID of the user
 * @param {string} tripId - The ID of the trip
 * @param {Object} travelData - Travel details including mode, time, etc.
 * @returns {Promise<string>} The ID of the newly created travel arrangement
 */
export const addTravel = async (userId, tripId, travelData) => {
  try {
    console.log('Adding travel with data:', travelData);  // Debug log
    const travelRef = collection(db, 'users', userId, 'trips', tripId, 'travel');
    const docRef = await addDoc(travelRef, {
      ...travelData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    console.log('Travel added with ID:', docRef.id);  // Debug log
    return docRef.id;
  } catch (error) {
    console.error('Error adding travel:', error);
    throw error;
  }
};

/**
 * Retrieves all travel arrangements for a trip
 * @param {string} userId - The ID of the user
 * @param {string} tripId - The ID of the trip
 * @returns {Promise<Array>} Array of travel arrangement objects
 */
export const getTravel = async (userId, tripId) => {
  try {
    console.log(`Fetching travel for user ${userId} and trip ${tripId}`);  // Debug log
    const travelRef = collection(db, 'users', userId, 'trips', tripId, 'travel');
    const querySnapshot = await getDocs(travelRef);

    console.log(`Found ${querySnapshot.size} travel documents`);  // Debug log

    const travel = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    console.log('Transformed travel data:', travel);  // Debug log
    return travel;
  } catch (error) {
    console.error('Error getting travel:', error);
    throw error;
  }
};

/**
 * Updates an existing travel arrangement
 * @param {string} userId - The ID of the user
 * @param {string} tripId - The ID of the trip
 * @param {string} travelId - The ID of the travel arrangement
 * @param {Object} travelData - Updated travel details
 */
export const updateTravel = async (userId, tripId, travelId, travelData) => {
  try {
    const travelRef = doc(db, 'users', userId, 'trips', tripId, 'travel', travelId);
    await updateDoc(travelRef, {
      ...travelData,
      updatedAt: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error updating travel: ', error);
    throw error;
  }
};

/**
 * Deletes a travel arrangement
 * @param {string} userId - The ID of the user
 * @param {string} tripId - The ID of the trip
 * @param {string} travelId - The ID of the travel arrangement to delete
 */
export const deleteTravel = async (userId, tripId, travelId) => {
  try {
    const travelRef = doc(db, 'users', userId, 'trips', tripId, 'travel', travelId);
    await deleteDoc(travelRef);
  } catch (error) {
    console.error('Error deleting travel: ', error);
    throw error;
  }
};

/**
 * Accommodation Management Section
 * Handles operations for lodging and accommodation arrangements
 * Includes hotel bookings, rentals, and other stay arrangements
 */

/**
 * Adds a new accommodation arrangement
 * @param {string} userId - The ID of the user
 * @param {string} tripId - The ID of the trip
 * @param {Object} accommodationData - Accommodation details including location, check-in/out dates, etc.
 * @returns {Promise<string>} The ID of the newly created accommodation arrangement
 */
export const addAccommodation = async (userId, tripId, accommodationData) => {
  try {
    console.log('Adding accommodation with data:', accommodationData);  // Debug log
    const accommodationRef = collection(db, 'users', userId, 'trips', tripId, 'accommodation');
    const docRef = await addDoc(accommodationRef, {
      ...accommodationData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    console.log('Accommodation added with ID:', docRef.id);  // Debug log
    return docRef.id;
  } catch (error) {
    console.error('Error adding accommodation: ', error);
    throw error;
  }
};

/**
 * Retrieves all accommodation arrangements for a trip
 * @param {string} userId - The ID of the user
 * @param {string} tripId - The ID of the trip
 * @returns {Promise<Array>} Array of accommodation objects
 */
export const getAccommodation = async (userId, tripId) => {
  try {
    console.log(`Fetching accommodation for user ${userId} and trip ${tripId}`);  // Debug log
    const accommodationRef = collection(db, 'users', userId, 'trips', tripId, 'accommodation');
    // Use createdAt as fallback if details.checkInDate is not available
    const querySnapshot = await getDocs(accommodationRef);

    console.log(`Found ${querySnapshot.size} accommodation documents`);  // Debug log

    const accommodation = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    console.log('Transformed accommodation data:', accommodation);  // Debug log
    return accommodation;
  } catch (error) {
    console.error('Error getting accommodation: ', error);
    throw error;
  }
};

/**
 * Updates an existing accommodation arrangement
 * @param {string} userId - The ID of the user
 * @param {string} tripId - The ID of the trip
 * @param {string} accommodationId - The ID of the accommodation
 * @param {Object} accommodationData - Updated accommodation details
 */
export const updateAccommodation = async (userId, tripId, accommodationId, accommodationData) => {
  try {
    console.log(`Updating accommodation ${accommodationId} with data:`, accommodationData);  // Debug log
    const accommodationRef = doc(db, 'users', userId, 'trips', tripId, 'accommodation', accommodationId);

    // Create a clean update payload by explicitly filtering out problematic values
    const updatePayload = {};
    const deleteFieldOperations = {};

    // Helper function to safely check if something might be a deleteField
    const mightBeDeleteField = (value) => {
      if (!value || typeof value !== 'object') return false;
      const str = String(value);
      return str.includes('deleteField') || str.includes('DeleteField') ||
        (value._methodName && value._methodName === 'deleteField');
    };

    // Process top-level fields - exclude details and problematic objects
    Object.entries(accommodationData).forEach(([key, value]) => {
      if (key !== 'details') {
        console.log(`DEBUG: Processing top-level field ${key}:`, value, typeof value);

        if (mightBeDeleteField(value)) {
          console.log(`DEBUG: Found deleteField operation for ${key}`);
          deleteFieldOperations[key] = true;
        } else if (value !== undefined) {
          updatePayload[key] = value;
        }
      }
    });

    // Process details fields with dot notation
    if (accommodationData.details) {
      Object.entries(accommodationData.details).forEach(([key, value]) => {
        console.log(`DEBUG: Processing details field ${key}:`, value, typeof value);

        if (mightBeDeleteField(value)) {
          console.log(`DEBUG: Found deleteField operation for details.${key}`);
          deleteFieldOperations[`details.${key}`] = true;
        } else if (value !== undefined) {
          updatePayload[`details.${key}`] = value;
        }
      });
    }

    // Add deleteField operations to the payload
    Object.keys(deleteFieldOperations).forEach(fieldPath => {
      console.log(`DEBUG: Adding deleteField() for ${fieldPath}`);
      updatePayload[fieldPath] = deleteField();
    });

    // Always update the timestamp
    updatePayload.updatedAt = new Date().toISOString();

    console.log('DEBUG: Final update payload:', updatePayload);
    console.log('DEBUG: DeleteField operations found:', Object.keys(deleteFieldOperations));

    await updateDoc(accommodationRef, updatePayload);
    console.log(`Accommodation ${accommodationId} updated successfully`);
  } catch (error) {
    console.error('Error updating accommodation: ', error);
    throw error;
  }
};

/**
 * Deletes an accommodation arrangement
 * @param {string} userId - The ID of the user
 * @param {string} tripId - The ID of the trip
 * @param {string} accommodationId - The ID of the accommodation to delete
 */
export const deleteAccommodation = async (userId, tripId, accommodationId) => {
  try {
    console.log(`Deleting accommodation ${accommodationId}`);  // Debug log
    const accommodationRef = doc(db, 'users', userId, 'trips', tripId, 'accommodation', accommodationId);
    await deleteDoc(accommodationRef);
    console.log(`Accommodation ${accommodationId} deleted successfully`);  // Debug log
  } catch (error) {
    console.error('Error deleting accommodation: ', error);
    throw error;
  }
};

/**
 * Additional accommodation helper functions
 */

/**
 * Retrieves a specific accommodation by ID
 * @param {string} userId - The ID of the user
 * @param {string} tripId - The ID of the trip
 * @param {string} accommodationId - The ID of the accommodation to retrieve
 * @returns {Promise<Object>} Accommodation object with its ID
 * @throws {Error} If accommodation not found
 */
export const getAccommodationById = async (userId, tripId, accommodationId) => {
  try {
    console.log(`Fetching accommodation ${accommodationId}`);
    const accommodationRef = doc(db, 'users', userId, 'trips', tripId, 'accommodation', accommodationId);
    const accommodationDoc = await getDoc(accommodationRef);

    if (!accommodationDoc.exists()) {
      console.log(`Accommodation ${accommodationId} not found`);
      return null;
    }

    return {
      id: accommodationDoc.id,
      ...accommodationDoc.data()
    };
  } catch (error) {
    console.error('Error getting accommodation by ID: ', error);
    throw error;
  }
};

/**
 * Retrieves accommodations within a specific date range
 * Useful for finding overlapping stays or available dates
 * @param {string} userId - The ID of the user
 * @param {string} tripId - The ID of the trip
 * @param {string} startDate - Start date in ISO format
 * @param {string} endDate - End date in ISO format
 * @returns {Promise<Array>} Array of accommodation objects within the date range
 */
export const getAccommodationByDateRange = async (userId, tripId, startDate, endDate) => {
  try {
    console.log(`Fetching accommodation in date range: ${startDate} to ${endDate}`);

    // Get all accommodation
    const allAccommodation = await getAccommodation(userId, tripId);

    // Convert dates for comparison
    const start = new Date(startDate);
    const end = new Date(endDate);

    // Filter accommodation that overlaps with the date range
    const filteredAccommodation = allAccommodation.filter(accommodation => {
      // Skip if dates are missing
      if (!accommodation.details?.checkInDate || !accommodation.details?.checkOutDate) {
        return false;
      }

      const checkIn = new Date(accommodation.details.checkInDate);
      const checkOut = new Date(accommodation.details.checkOutDate);

      // Check for overlap: starts before end date AND ends after start date
      return checkIn <= end && checkOut >= start;
    });

    console.log(`Found ${filteredAccommodation.length} accommodations in date range`);
    return filteredAccommodation;
  } catch (error) {
    console.error('Error getting accommodation by date range: ', error);
    throw error;
  }
};

/**
 * Performs batch updates on multiple accommodation arrangements
 * Useful for updating multiple records in a single transaction
 * @param {string} userId - The ID of the user
 * @param {string} tripId - The ID of the trip
 * @param {Array<Object>} updates - Array of update objects containing accommodationId and data
 * @returns {Promise<void>}
 */
export const batchUpdateAccommodation = async (userId, tripId, updates) => {
  try {
    console.log(`Batch updating ${updates.length} accommodations`);

    const batch = writeBatch(db);

    updates.forEach(update => {
      const { id, ...data } = update;
      if (!id) {
        throw new Error('Accommodation ID is required for batch update');
      }

      const accommodationRef = doc(db, 'users', userId, 'trips', tripId, 'accommodation', id);
      batch.update(accommodationRef, {
        ...data,
        updatedAt: new Date().toISOString()
      });
    });

    await batch.commit();
    console.log('Batch update completed successfully');

    return true;
  } catch (error) {
    console.error('Error batch updating accommodations: ', error);
    throw error;
  }
};

/**
 * Document Management Section
 * Handles operations for documents associated with a trip
 */

/**
 * Retrieves all documents for a trip
 * @param {string} userId - The ID of the user
 * @param {string} tripId - The ID of the trip
 * @returns {Promise<Array>} Array of document objects
 */
export const getDocuments = async (userId, tripId) => {
  try {
    const documentsRef = collection(db, 'users', userId, 'trips', tripId, 'documents');
    const q = query(documentsRef, orderBy('uploadedAt', 'desc'));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting documents: ', error);
    // If collection doesn't exist, return empty array instead of throwing
    if (error.code === 'not-found') return [];
    throw error;
  }
};
